import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import useUserIdParam from "../../hooks/useUserIdParam";
import useLoanOfficerStatus from "../../hooks/useLoanOfficerStatus";
import FormatMoney from "../../helpers";
import ValidationSchema from "../validation";
import createAxiosInstance from "../../api/api";
import PostLandingData from "../../api/PostLandingData";
import PostResultData from "../../api/PostResultData";
import Modal from "../Modal";
import {
  Label,
  ErrorText,
  TextInput,
  SelectInput,
  Btn,
  Spinner,
  CountUp,
  Loading,
  NoLoanOfficer
} from "./FormControls";
import {
  Page,
  Container,
  HeaderWrap,
  Eyebrow,
  Title,
  Section,
  FieldGroup,
  NoAccessBanner,
  ResultWrap,
  ResultsHeading,
  ResultsRow,
  ResultCard,
  ResultLabel,
  ResultValue,
  Disclaimer,
  ShareRow,
  globalStyles
} from "./Form.styles";

const NET_KEY = "netReverseMortgageFundsAfterPayingOffCurrentMortgageBalances";

const roundK = (n) => Math.round(Number(n) / 1000) * 1000;

const CalculationForm = ({ open, setOpen }) => {
  const initialValues = {
    youngestAge: "",
    propertyValue: "",
    mortgageBalances: "",
    state: ""
  };

  const userId = useUserIdParam();
  const axiosInstance = createAxiosInstance();
  const { loanOfficer, loaded, errorMessage } = useLoanOfficerStatus(axiosInstance);
  const officer = loanOfficer || {};
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState("idle"); // idle | loading | loaded | error
  const [fundsAvailable, setFundsAvailable] = useState(null);
  const [qrCashAtClosing, setQrCashAtClosing] = useState(null);
  const [qrQualifies, setQrQualifies] = useState(null);
  const [qualified, setQualified] = useState({
    qualifies: null,
    message: null
  });

  const [scenarioData, setScenarioData] = useState(null);
  const [formValues, setFormValues] = useState(initialValues);

  useEffect(() => {
    if (errorMessage) enqueueSnackbar(errorMessage);
  }, [errorMessage]); // eslint-disable-line

  const openModal = () => setOpen(true);

  useEffect(() => {
    if (!scenarioData) return;
    if (scenarioData.scenarioData?.finalValues?.otherValues?.[NET_KEY]) {
      setFundsAvailable(`${scenarioData.scenarioData.finalValues.otherValues[NET_KEY]}`);

      if (scenarioData.quantumReverseLoanEstimate?.AvailableCashAtClosing) {
        setQrQualifies(true);
        setQrCashAtClosing(`${scenarioData.quantumReverseLoanEstimate.AvailableCashAtClosing}`);
      } else {
        setQrQualifies(false);
      }

      setQualified({ qualifies: true, message: null });
    } else {
      const message =
        scenarioData.doesNotQualifyReasons?.[0] || "Does not qualify for reverse mortgage";
      setFundsAvailable(null);
      setQrCashAtClosing(null);
      setQualified({ qualifies: false, message });
    }
  }, [scenarioData]); // eslint-disable-line

  const SubmitForm = (values) => {
    setStatus("loading");
    axiosInstance
      .post("landing-form", PostLandingData(values, userId))
      .then((response) => {
        setScenarioData(response.data.allScenarioData);
        setStatus("loaded");
      })
      .catch((e) => {
        setScenarioData(null);
        setStatus("error");
        enqueueSnackbar(e.message || "Something went wrong. Please try again.");
      });
  };

  const ShareResult = (values, { setSubmitting }) => {
    axiosInstance
      .post("public/calculation-results/", PostResultData(values, formValues, userId, scenarioData))
      .then(() => {
        setOpen(false);
        enqueueSnackbar("Data shared via email");
      })
      .catch((e) => {
        enqueueSnackbar(e.message);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (!loaded) {
    return <Loading />;
  }

  if (!loanOfficer) {
    return <NoLoanOfficer />;
  }

  return (
    <Page>
      <style>{globalStyles}</style>

      <Container>
        <HeaderWrap>
          <Eyebrow>GRIPH Reverse Mortgage Estimates</Eyebrow>
          <Title>
            Loan Officer:{" "}
            {officer.firstName
              ? `${officer.firstName} ${officer.lastName}`
              : "No loan officer with this id"}
          </Title>
        </HeaderWrap>

        <Section>
          <Formik
            initialValues={initialValues}
            validationSchema={ValidationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={SubmitForm}>
            {({ values, errors, touched, handleChange, setFieldValue, setFieldError }) => {
              const digits = (s) => s.replace(/[^\d]/g, "");
              return (
                <Form>
                  <FieldGroup>
                    <Label htmlFor="youngestAge">Youngest Age</Label>
                    <TextInput
                      id="youngestAge"
                      name="youngestAge"
                      autoFocus
                      numeric
                      suffix="yrs"
                      value={values.youngestAge}
                      invalid={!!(errors.youngestAge && touched.youngestAge)}
                      onChange={(e) => {
                        const next = digits(e.target.value).slice(0, 3);
                        setFieldValue("youngestAge", next);
                        setFieldError("youngestAge", undefined);
                        setFormValues((prev) => ({ ...prev, youngestAge: next }));
                      }}
                    />
                    {errors.youngestAge && touched.youngestAge && (
                      <ErrorText>{errors.youngestAge}</ErrorText>
                    )}
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="propertyValue">Property Value</Label>
                    <TextInput
                      id="propertyValue"
                      name="propertyValue"
                      numeric
                      prefix="$"
                      placeholder="0"
                      value={FormatMoney(values.propertyValue)}
                      invalid={!!(errors.propertyValue && touched.propertyValue)}
                      onChange={(e) => {
                        const next = digits(e.target.value).slice(0, 9);
                        setFieldValue("propertyValue", next);
                        setFieldError("propertyValue", undefined);
                        setFormValues((prev) => ({ ...prev, propertyValue: next }));
                      }}
                    />
                    {errors.propertyValue && touched.propertyValue && (
                      <ErrorText>{errors.propertyValue}</ErrorText>
                    )}
                  </FieldGroup>

                  <FieldGroup>
                    <Label htmlFor="mortgageBalances">Mortgage Balances</Label>
                    <TextInput
                      id="mortgageBalances"
                      name="mortgageBalances"
                      numeric
                      prefix="$"
                      placeholder="0"
                      value={FormatMoney(values.mortgageBalances)}
                      invalid={!!(errors.mortgageBalances && touched.mortgageBalances)}
                      onChange={(e) => {
                        const next = digits(e.target.value).slice(0, 9);
                        setFieldValue("mortgageBalances", next);
                        setFieldError("mortgageBalances", undefined);
                        setFormValues((prev) => ({ ...prev, mortgageBalances: next }));
                      }}
                    />
                    {errors.mortgageBalances && touched.mortgageBalances && (
                      <ErrorText>{errors.mortgageBalances}</ErrorText>
                    )}
                  </FieldGroup>

                  <FieldGroup spacing={32}>
                    <Label htmlFor="state">State</Label>
                    <SelectInput
                      id="state"
                      name="state"
                      value={values.state}
                      invalid={!!(errors.state && touched.state)}
                      onChange={(e) => {
                        handleChange(e);
                        setFieldError("state", undefined);
                        setFormValues((prev) => ({ ...prev, state: e.target.value }));
                      }}>
                      <option value="" disabled>
                        Select a state
                      </option>
                      {officer?.states?.map((s) => (
                        <option key={s.abbreviation} value={s.state}>
                          {s.state}
                        </option>
                      ))}
                    </SelectInput>
                    {errors.state && touched.state && <ErrorText>{errors.state}</ErrorText>}
                  </FieldGroup>

                  {officer.paidPWA ? (
                    <Btn type="submit" full disabled={status === "loading"}>
                      {status === "loading" ? (
                        <>
                          <Spinner />
                          Submitting
                        </>
                      ) : (
                        <>
                          Get results
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round">
                            <path d="M5 12h14M13 6l6 6-6 6" />
                          </svg>
                        </>
                      )}
                    </Btn>
                  ) : (
                    <NoAccessBanner>No access for this loan officer</NoAccessBanner>
                  )}
                </Form>
              );
            }}
          </Formik>

          {/* -------- result: qualified -------- */}
          {status === "loaded" && qualified.qualifies && (
            <ResultWrap aria-live="polite">
              <ResultsHeading>Your net funds, after paying off liens</ResultsHeading>
              <ResultsRow>
                <ResultCard>
                  <ResultLabel>FHA Reverse Mortgage</ResultLabel>
                  <ResultValue>
                    <CountUp target={roundK(fundsAvailable)} />
                  </ResultValue>

                  {Number(formValues.youngestAge) < 62 && (
                    <Disclaimer>
                      For the FHA reverse mortgage, GRIPH assumes the youngest is an ENBS and the
                      Borrower is 62
                    </Disclaimer>
                  )}
                </ResultCard>

                <ResultCard secondary>
                  <ResultLabel secondary>Conventional Reverse Mortgage</ResultLabel>
                  <ResultValue secondary>
                    {qrQualifies ? (
                      <CountUp target={roundK(qrCashAtClosing)} />
                    ) : (
                      <span style={{ fontSize: 20 }}>
                        Conventional Reverse is not available due to age and/or state restrictions.
                      </span>
                    )}
                  </ResultValue>
                </ResultCard>
              </ResultsRow>

              <ShareRow>
                <Btn variant="outline" onClick={openModal}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
                  </svg>
                  Share
                </Btn>
              </ShareRow>

              <Modal open={open} setOpen={setOpen} onSubmit={ShareResult} />
            </ResultWrap>
          )}

          {/* -------- result: not qualified -------- */}
          {status === "loaded" && qualified.qualifies === false && (
            <ResultWrap aria-live="polite">
              <ResultCard>
                <ResultLabel>Net funds available, after paying off liens</ResultLabel>
                <ResultValue danger>{qualified.message}</ResultValue>
              </ResultCard>
            </ResultWrap>
          )}
        </Section>
      </Container>
    </Page>
  );
};

export default CalculationForm;
